import React, { useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '../contexts/ThemeContext';
import { useCart } from '../contexts/CartContext';
import { useContent } from '../hooks/useContent';
import { useLanguage } from '../contexts/LanguageContext';

const navItemClass = ({ isActive }) =>
  `flex items-center px-4 py-3 rounded-lg transition-colors duration-200 ${
    isActive ? 'bg-dark-700 text-white' : 'text-gray-300 hover:bg-dark-800 hover:text-white'
  }`;

const DashboardSidebar = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const { itemCount } = useCart();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { language, setLanguage } = useLanguage();
  const { content } = useContent('dashboardSidebar');

  const t = content || {
    nav: { home: 'Générateur', myCodes: 'Mes codes', vcard: 'Mes VCards', gs1: 'GS1 Digital Link', analytics: 'Statistiques', shop: 'Boutique', account: 'Mon compte', cart: 'Panier' },
    actions: { logout: 'Se déconnecter', backToSite: 'Retour au site' },
    theme: { toDark: 'Passer en mode sombre', toLight: 'Passer en mode clair' },
    gs1Tooltip: 'Non compris dans votre abonnement',
    language: { label: 'Langue :', switchTo: 'Switch to English' },
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);
  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  return (
    <>
      <div className="md:hidden fixed top-0 left-0 right-0 h-16 bg-black border-b border-dark-700 flex items-center justify-between px-4 z-50">
        <Link to="/" className="flex items-center space-x-2">
          <img
            src="/images/logo.png"
            alt="Quikr.it Logo"
            className="w-36 h-16 object-contain"
          />
        </Link>
        <button
          onClick={toggleMobileMenu}
          className="text-white p-2 hover:bg-dark-700 rounded-lg transition-colors"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {isMobileMenuOpen ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            )}
          </svg>
        </button>
      </div>

      {isMobileMenuOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={closeMobileMenu}
        />
      )}

      <aside className="fixed inset-y-0 left-0 w-64 bg-black border-r border-dark-700 hidden md:flex flex-col z-40">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-90 pointer-events-none -z-10"
          style={{ backgroundImage: "url('/images/sidebarsdash.jpg')" }}
        />
        <div className="h-16 flex items-center px-4 border-b border-dark-700">
          <Link to="/" className="flex items-center space-x-2">
            <img
              src="/images/logo.png"
              alt="Quikr.it Logo"
              className="w-36 h-16 object-contain"
            />
          </Link>
        </div>
        <nav className="p-4 space-y-2 flex-1 overflow-y-auto">
          <NavLink to="/dashboard" end className={navItemClass}>
            <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2 4 4 8-8 4 4" />
            </svg>
            {t.nav.home}
          </NavLink>
          <NavLink to="/dashboard/my-codes" className={navItemClass}>
            <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5h6v6H4zM14 5h6v6h-6zM4 15h6v6H4zM14 15h6v6h-6z" />
            </svg>
            {t.nav.myCodes}
          </NavLink>
	  <NavLink to="/dashboard/vcard" className={navItemClass}>
	  <svg className="w-5 h-5 mr-3" fill="none" xmlns="http://www.w3.org/2000/svg" width="2em" height="2em" viewBox="0 0 640 640">
	  <path fill="currentColor" d="M544 144c8.8 0 16 7.2 16 16v320c0 8.8-7.2 16-16 16H96c-8.8 0-16-7.2-16-16V160c0-8.8 7.2-16 16-16zM96 96c-35.3 0-64 28.7-64 64v320c0 35.3 28.7 64 64 64h448c35.3 0 64-28.7 64-64V160c0-35.3-28.7-64-64-64zm144 216c30.9 0 56-25.1 56-56s-25.1-56-56-56s-56 25.1-56 56s25.1 56 56 56m-32 40c-44.2 0-80 35.8-80 80c0 8.8 7.2 16 16 16h192c8.8 0 16-7.2 16-16c0-44.2-35.8-80-80-80zm200-144c-13.3 0-24 10.7-24 24s10.7 24 24 24h80c13.3 0 24-10.7 24-24s-10.7-24-24-24zm0 96c-13.3 0-24 10.7-24 24s10.7 24 24 24h80c13.3 0 24-10.7 24-24s-10.7-24-24-24z" />
	  </svg>
	  {t.nav.vcard}
	  </NavLink>
          <div
            className="flex items-center px-4 py-3 rounded-lg text-gray-500 cursor-not-allowed opacity-60"
            title={t.gs1Tooltip}
          >
            <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
            </svg>
            {t.nav.gs1}
          </div>
          <NavLink to="/dashboard/analytics" className={navItemClass}>
            <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19V6m-4 13V10m8 9V4m4 15v-7" />
            </svg>
            {t.nav.analytics}
          </NavLink>
          <NavLink to="/dashboard/shop" className={navItemClass}>
            <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
            </svg>
            {t.nav.shop}
          </NavLink>
          <NavLink to="/dashboard/cart" className={navItemClass}>
            <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <span className="flex items-center justify-between flex-1">
              {t.nav.cart}
              {itemCount > 0 && (
                <span className="ml-2 px-2 py-0.5 text-xs font-semibold rounded-full bg-primary-600 text-white">
                  {itemCount}
                </span>
              )}
            </span>
          </NavLink>
          <NavLink to="/dashboard/account" className={navItemClass}>
            <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.121 17.804A13.937 13.937 0 0112 15c2.5 0 4.847.655 6.879 1.804M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            {t.nav.account}
          </NavLink>

        </nav>
        <div className="p-4 border-t border-dark-700 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <button
              onClick={toggleTheme}
              className="flex-1 bg-dark-800 border border-dark-700 hover:bg-dark-700 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200 flex items-center justify-center"
              title={isDark ? t.theme.toLight : t.theme.toDark}
            >
              {isDark ? (
                <svg className="w-5 h-5 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              ) : (
                <svg className="w-5 h-5 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
              )}
            </button>
            <div className="flex bg-dark-800 border border-dark-700 rounded-lg overflow-hidden">
              <button
                onClick={() => setLanguage('fr')}
                className={`flex items-center px-2 py-1.5 text-xs font-bold transition-colors duration-200 ${
                  language === 'fr' ? 'bg-primary-600 text-white' : 'text-gray-400 hover:bg-dark-700 hover:text-white'
                }`}
              >
                <img src="/images/flag-fr.svg" alt="FR" className="w-5 h-3.5 rounded-sm object-cover" />
              </button>
              <button
                onClick={() => setLanguage('en')}
                className={`flex items-center px-2 py-1.5 text-xs font-bold transition-colors duration-200 ${
                  language === 'en' ? 'bg-primary-600 text-white' : 'text-gray-400 hover:bg-dark-700 hover:text-white'
                }`}
              >
                <img src="/images/flag-us.svg" alt="EN" className="w-5 h-3.5 rounded-sm object-cover" />
              </button>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full bg-secondary-600 hover:bg-secondary-700 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200 flex items-center justify-center"
          >
            {t.actions.logout}
          </button>
          <button
            onClick={() => navigate('/')}
            className="w-full bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200"
          >
            {t.actions.backToSite}
          </button>
        </div>
      </aside>

      {/* Menu mobile */}
      <aside className={`md:hidden fixed inset-y-0 left-0 w-64 bg-black border-r border-dark-700 flex flex-col z-50 transform transition-transform duration-300 ease-in-out ${
        isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-40 pointer-events-none"
          style={{ backgroundImage: "url('/images/sidebarsdash.jpg')" }}
        />
        <div className="h-16 flex items-center px-4 border-b border-dark-700">
          <Link to="/" className="flex items-center space-x-2">
            <img
              src="/images/logo.png"
              alt="Quikr.it Logo"
              className="w-36 h-16 object-contain"
            />
          </Link>
        </div>
        <nav className="p-4 space-y-2 flex-1 overflow-y-auto">
          <NavLink to="/dashboard" end className={navItemClass} onClick={closeMobileMenu}>
            <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2 4 4 8-8 4 4" />
            </svg>
            {t.nav.home}
          </NavLink>
          <NavLink to="/dashboard/my-codes" className={navItemClass} onClick={closeMobileMenu}>
            <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5h6v6H4zM14 5h6v6h-6zM4 15h6v6H4zM14 15h6v6h-6z" />
            </svg>
            {t.nav.myCodes}
          </NavLink>
	  <NavLink to="/dashboard/vcard" className={navItemClass} onClick={closeMobileMenu}>
	  <svg className="w-5 h-5 mr-3" fill="none" xmlns="http://www.w3.org/2000/svg" width="2em" height="2em" viewBox="0 0 640 640">
	  <path fill="currentColor" d="M544 144c8.8 0 16 7.2 16 16v320c0 8.8-7.2 16-16 16H96c-8.8 0-16-7.2-16-16V160c0-8.8 7.2-16 16-16zM96 96c-35.3 0-64 28.7-64 64v320c0 35.3 28.7 64 64 64h448c35.3 0 64-28.7 64-64V160c0-35.3-28.7-64-64-64zm144 216c30.9 0 56-25.1 56-56s-25.1-56-56-56s-56 25.1-56 56s25.1 56 56 56m-32 40c-44.2 0-80 35.8-80 80c0 8.8 7.2 16 16 16h192c8.8 0 16-7.2 16-16c0-44.2-35.8-80-80-80zm200-144c-13.3 0-24 10.7-24 24s10.7 24 24 24h80c13.3 0 24-10.7 24-24s-10.7-24-24-24zm0 96c-13.3 0-24 10.7-24 24s10.7 24 24 24h80c13.3 0 24-10.7 24-24s-10.7-24-24-24z" />
	  </svg>
	  {t.nav.vcard}
	  </NavLink>
          <NavLink to="/dashboard/gs1" className={navItemClass} onClick={closeMobileMenu}>
            <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
            </svg>
            {t.nav.gs1}
          </NavLink>
          <NavLink to="/dashboard/analytics" className={navItemClass} onClick={closeMobileMenu}>
            <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19V6m-4 13V10m8 9V4m4 15v-7" />
            </svg>
            {t.nav.analytics}
          </NavLink>
          <NavLink to="/dashboard/shop" className={navItemClass} onClick={closeMobileMenu}>
            <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
            </svg>
            {t.nav.shop}
          </NavLink>
          <NavLink to="/dashboard/cart" className={navItemClass} onClick={closeMobileMenu}>
            <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <span className="flex items-center justify-between flex-1">
              {t.nav.cart}
              {itemCount > 0 && (
                <span className="ml-2 px-2 py-0.5 text-xs font-semibold rounded-full bg-primary-600 text-white">
                  {itemCount}
                </span>
              )}
            </span>
          </NavLink>
          <NavLink to="/dashboard/account" className={navItemClass} onClick={closeMobileMenu}>
            <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.121 17.804A13.937 13.937 0 0112 15c2.5 0 4.847.655 6.879 1.804M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            {t.nav.account}
          </NavLink>
        </nav>
        <div className="p-4 border-t border-dark-700 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <button
              onClick={toggleTheme}
              className="flex-1 bg-dark-800 border border-dark-700 hover:bg-dark-700 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200 flex items-center justify-center"
              title={isDark ? t.theme.toLight : t.theme.toDark}
            >
              {isDark ? (
                <svg className="w-5 h-5 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              ) : (
                <svg className="w-5 h-5 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
              )}
            </button>
            <div className="flex bg-dark-800 border border-dark-700 rounded-lg overflow-hidden">
              <button
                onClick={() => setLanguage('fr')}
                className={`flex items-center px-2 py-1.5 text-xs font-bold transition-colors duration-200 ${
                  language === 'fr' ? 'bg-primary-600 text-white' : 'text-gray-400 hover:bg-dark-700 hover:text-white'
                }`}
              >
                <img src="/images/flag-fr.svg" alt="FR" className="w-5 h-3.5 rounded-sm object-cover" />
              </button>
              <button
                onClick={() => setLanguage('en')}
                className={`flex items-center px-2 py-1.5 text-xs font-bold transition-colors duration-200 ${
                  language === 'en' ? 'bg-primary-600 text-white' : 'text-gray-400 hover:bg-dark-700 hover:text-white'
                }`}
              >
                <img src="/images/flag-us.svg" alt="EN" className="w-5 h-3.5 rounded-sm object-cover" />
              </button>
            </div>
          </div>
          <button
            onClick={() => { handleLogout(); closeMobileMenu(); }}
            className="w-full bg-secondary-600 hover:bg-secondary-700 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200 flex items-center justify-center"
          >
            {t.actions.logout}
          </button>
          <button
            onClick={() => { navigate('/'); closeMobileMenu(); }}
            className="w-full bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200"
          >
            {t.actions.backToSite}
          </button>
        </div>
      </aside>
    </>
  );
};

export default DashboardSidebar;
